import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const ChatModal = ({ friend, onClose }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api.get(`/messages/${friend._id}`);
      setMessages(res.data);
      await api.patch(`/messages/${friend._id}/read`);
    } catch {}
  }, [friend._id]);

  useEffect(() => {
    fetchMessages();
    intervalRef.current = setInterval(fetchMessages, 5000);
    return () => clearInterval(intervalRef.current);
  }, [fetchMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/messages/${friend._id}`, { content: input.trim() });
      setMessages(m => [...m, res.data]);
      setInput('');
    } catch {} finally { setSending(false); }
  };

  const isMe = (msg) => msg.sender === user?.id || msg.sender?._id === user?.id || String(msg.sender) === String(user?.id);

  return (
    <>
      <style>{`
        @keyframes cm-backdrop { from{opacity:0} to{opacity:1} }
        @keyframes cm-card {
          0%   { transform:scale(0.5) translateY(40px); opacity:0 }
          65%  { transform:scale(1.04) translateY(-4px); opacity:1 }
          82%  { transform:scale(0.98) translateY(2px) }
          100% { transform:scale(1) translateY(0); opacity:1 }
        }
        @keyframes cm-stripe { 0%{transform:scaleX(0)} 100%{transform:scaleX(1)} }
        @keyframes cm-in { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
        style={{ animation:'cm-backdrop 0.25s ease forwards', background:'rgba(0,0,0,0.90)', backdropFilter:'blur(10px)' }}>
        <div className="relative w-full max-w-sm rounded-2xl overflow-hidden flex flex-col"
          style={{
            border:'2px solid #e10600',
            background:'linear-gradient(160deg,#0c0c10 0%,#e1060015 100%)',
            boxShadow:'0 0 50px #e1060045,0 0 100px #e1060018,inset 0 0 30px #e1060006',
            animation:'cm-card 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.05s both',
            height:'480px',
          }}>

          {/* Racing stripe */}
          <div style={{ height:4, background:'linear-gradient(90deg,#e10600 0%,transparent 80%)',
            animation:'cm-stripe 0.5s 0.15s ease-out both', transformOrigin:'left', flexShrink:0 }} />

          {/* Checkered accent */}
          <div className="absolute top-0 right-0 flex opacity-20 pointer-events-none">
            {[...Array(6)].map((_,i) => (
              <div key={i} style={{ width:10, height:10, background: i%2===0?'#fff':'transparent' }} />
            ))}
          </div>

          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 shrink-0"
            style={{ animation:'cm-in 0.35s 0.3s ease both', opacity:0 }}>
            <div className="w-9 h-9 rounded-full overflow-hidden shrink-0"
              style={{ border:'2px solid #e10600', boxShadow:'0 0 8px #e1060040' }}>
              {friend.avatar
                ? <img src={friend.avatar} alt="" className="w-full h-full object-cover" />
                : <div className="w-full h-full flex items-center justify-center text-xs font-black"
                    style={{ background:'#e1060020', color:'#e10600' }}>
                    {friend.username?.[0]?.toUpperCase()}
                  </div>
              }
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-black text-sm truncate">{friend.username}</p>
              <p className="text-zinc-500 text-[10px]">Niveau {friend.level ?? 0}</p>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
              style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.1)' }}>
              <span className="text-zinc-400 text-sm leading-none">✕</span>
            </button>
          </div>

          {/* Divider */}
          <div className="mx-4 h-px" style={{ background:'linear-gradient(90deg,#e10600 0%,transparent 80%)', opacity:0.3 }} />

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2"
            style={{ animation:'cm-in 0.35s 0.4s ease both', opacity:0 }}>
            {messages.length === 0 && (
              <p className="text-zinc-600 text-xs text-center mt-8">Commencez la conversation !</p>
            )}
            {messages.map((msg, i) => {
              const mine = isMe(msg);
              return (
                <div key={msg._id || i} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${mine ? 'rounded-br-sm' : 'rounded-bl-sm'}`}
                    style={mine
                      ? { background:'#e10600', color:'#fff', boxShadow:'0 2px 12px #e1060035' }
                      : { background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.09)', color:'#e4e4e7' }}>
                    <p className="leading-snug">{msg.content}</p>
                    <p className={`text-[9px] mt-0.5 ${mine ? 'text-red-200' : 'text-zinc-500'} text-right`}>
                      {new Date(msg.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="h-px mx-4" style={{ background:'linear-gradient(90deg,#e10600 0%,transparent 80%)', opacity:0.3 }} />
          <form onSubmit={handleSend} className="px-4 py-3 flex gap-2 shrink-0"
            style={{ animation:'cm-in 0.35s 0.5s ease both', opacity:0 }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Ton message..."
              maxLength={1000}
              className="flex-1 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none"
              style={{ background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', transition:'border-color 0.2s' }}
              onFocus={e => e.target.style.borderColor='#e10600'}
              onBlur={e => e.target.style.borderColor='rgba(255,255,255,0.12)'}
            />
            <button type="submit" disabled={!input.trim() || sending}
              className="font-black px-4 py-2 rounded-xl transition-all text-sm disabled:opacity-40"
              style={{ background:'#e10600', color:'#fff', boxShadow:'0 2px 16px #e1060050' }}>
              →
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default ChatModal;
